/**
 * Cookie / Privacy consent management.
 * Stores user preferences in localStorage and broadcasts changes via custom events.
 */

export const CONSENT_VERSION = 1;
export const CONSENT_STORAGE_KEY = "althaus-cookie-consent";
export const CONSENT_CHANGED_EVENT = "cookie-consent-changed";
export const OPEN_CONSENT_SETTINGS_EVENT = "open-cookie-settings";

export type CategoryKey = "essential" | "functional" | "marketing";

export type ServiceKey =
  | "consentManager"
  | "cloudflare"
  | "googleFonts"
  | "googleMaps"
  | "ostendis"
  | "googleAnalytics"
  | "googleTagManager"
  | "googleAds";

export interface ServiceDefinition {
  key: ServiceKey;
  category: CategoryKey;
}

export const SERVICE_DEFINITIONS: ServiceDefinition[] = [
  { key: "consentManager", category: "essential" },
  { key: "cloudflare", category: "functional" },
  { key: "googleFonts", category: "functional" },
  { key: "googleMaps", category: "functional" },
  { key: "ostendis", category: "functional" },
  { key: "googleAnalytics", category: "marketing" },
  { key: "googleTagManager", category: "marketing" },
  { key: "googleAds", category: "marketing" },
];

export interface ConsentState {
  version: number;
  timestamp: string;
  categories: Record<CategoryKey, boolean>;
  services: Record<ServiceKey, boolean>;
}

export const DEFAULT_CATEGORIES: Record<CategoryKey, boolean> = {
  essential: true,
  functional: false,
  marketing: false,
};

export function getDefaultServices(allow: boolean = false): Record<ServiceKey, boolean> {
  const out = {} as Record<ServiceKey, boolean>;
  SERVICE_DEFINITIONS.forEach((s) => {
    out[s.key] = s.category === "essential" ? true : allow;
  });
  return out;
}

export function getConsent(): ConsentState | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ConsentState;
    if (!parsed || parsed.version !== CONSENT_VERSION) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function setConsent(
  categories: Record<CategoryKey, boolean>,
  services: Record<ServiceKey, boolean>,
): ConsentState {
  const state: ConsentState = {
    version: CONSENT_VERSION,
    timestamp: new Date().toISOString(),
    categories: { ...categories, essential: true },
    services: { ...services, consentManager: true },
  };
  if (typeof window !== "undefined") {
    window.localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(state));
    window.dispatchEvent(new CustomEvent(CONSENT_CHANGED_EVENT, { detail: state }));
  }
  return state;
}

export function acceptAll(): ConsentState {
  return setConsent(
    { essential: true, functional: true, marketing: true },
    getDefaultServices(true),
  );
}

export function hasConsent(service: ServiceKey): boolean {
  const c = getConsent();
  if (!c) return false;
  return Boolean(c.services[service]);
}

export function openConsentSettings() {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(OPEN_CONSENT_SETTINGS_EVENT));
}
